import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, CircularProgress, Alert } from '@mui/joy';
import { useParams } from 'react-router-dom';
import { updateApplicantPublic } from '../../apiconfig/api';

const SetInactive: React.FC = () => {
  const { applicant_id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleSetInactive = async () => {
      if (!applicant_id) {
        setError("Missing person ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Update person to set isActive = false (don't update lastActiveAt)
        await updateApplicantPublic(applicant_id, {
          isActive: false
        });
        
        setSuccess(true);
      } catch (err: any) {
        console.error("Error setting inactive status:", err);
        setError(err.message || "Failed to update your status. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    handleSetInactive();
  }, [applicant_id]);

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f4f4f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container maxWidth="sm">
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '16px', 
            p: 4, 
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <CircularProgress size="lg" sx={{ mb: 2 }} />
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Updating your status...
            </Typography>
            <Typography level="body-md" sx={{ color: '#7B7F85', mt: 1 }}>
              Please wait a moment.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f4f4f4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      <Container maxWidth="md">
        <Box sx={{ 
          bgcolor: 'white', 
          borderRadius: '16px', 
          p: 4, 
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          {error ? (
            <>
              <Alert color="danger" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
                Unable to Update Status
              </Typography>
              <Typography level="body-md" sx={{ color: '#7B7F85', mb: 3 }}>
                We encountered an issue while updating your status. Please try again or contact support if the problem persists.
              </Typography>
            </>
          ) : success ? (
            <>
              <Box sx={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                bgcolor: '#2e2d7e', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <Typography sx={{ color: 'white', fontSize: '2rem' }}>✓</Typography>
              </Box>
              
              <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
                Thank You for Using Geek
              </Typography>
              
              <Typography level="body-lg" sx={{ color: '#7B7F85', mb: 3, lineHeight: 1.6 }}>
                We appreciate you being part of the Geek community. Your profile has been updated to inactive.
              </Typography>

              <Box sx={{ 
                bgcolor: '#F8F9FA', 
                borderRadius: '12px', 
                p: 3, 
                mb: 3
              }}>
                <Typography level="body-md" sx={{ color: '#1A1B1D', fontWeight: 500, mb: 1 }}>
                  Remember, we're here to help!
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85', lineHeight: 1.6 }}>
                  When you're ready to start your next job search, we'll be here to support you every step of the way. 
                  Just reach out and we'll help you get started again.
                </Typography>
              </Box>

              <Typography level="body-sm" sx={{ color: '#7B7F85', fontStyle: 'italic' }}>
                Best of luck in your current role, and we look forward to working with you again in the future!
              </Typography>
            </>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
};

export default SetInactive;

